import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback, useMemo, useState } from 'react';
import { Loading } from '../../common/Loading';
import PreviewPanel from './components/PreviewPanel';
import { VisitCardLineTable } from './components/VisitCardLineTable';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import styles from './VisitCardSettingsPage.module.scss';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import {
  CalendarType,
  VisitCardLineStyleDto,
  QueryFactory,
  VisitCardSettingsDto,
} from '@lmt/shared-types';

import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import {
  expressionErrorString,
  visitCardSettingsYupSchema,
} from './components/common';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import i18next from 'i18next';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useEventCallback } from '@mui/material';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { SubmitButton } from '../../common/ui-kit/buttons/SubmitButton';
import { isEmpty } from '../../measurements/copy-datablock-field/common';

const calendarTypes = [CalendarType.Day, CalendarType.Week, CalendarType.Month];

export const VisitCardSettingsPage: React.FC = React.memo(
  function VisitCardSettingsPage() {
    const i18n = useScopedTranslation('calendar.visit_card_settings');
    const queryCache = useQueryClient();
    const modals = useModal();
    const [visitSearchErrors, setVisitSearchErrors] = useState<string[]>([]);

    const [calendarType, setCalendarType] = useState<CalendarType>(
      CalendarType.Week,
    );

    const nameof = nameofFactory<VisitCardSettingsDto>();

    const visitCardSettingsQuery =
      QueryFactory.VisitCardSettingsQuery.useGetQuery();

    const form = useAdvancedForm<VisitCardSettingsDto>(
      useCallback(
        async (data) => {
          if (isValidationErrors || !isEmpty(visitSearchErrors)) {
            return;
          }
          try {
            await QueryFactory.VisitCardSettingsClient.patch(data);
            await queryCache.invalidateQueries({
              queryKey: QueryFactory.VisitCardSettingsQuery.getQueryKey(),
            });
            reset(data);
          } catch (error: any) {
            await modals.showError({ text: error.message });
          }
        },
        [visitCardSettingsQuery.data, calendarType],
      ),
      visitCardSettingsQuery.data,
      {
        resolver: yupResolver(visitCardSettingsYupSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: {
          dayLineStyles: [],
          monthLineStyles: [],
          weekLineStyles: [],
        },
      },
    );

    const { watch, setValue, getValues, formState, reset, clearErrors } = form;
    const { dirtyFields } = form.formState;
    const [dayLineStyles, monthLineStyles, weekLineStyles] = watch([
      'dayLineStyles',
      'monthLineStyles',
      'weekLineStyles',
    ]);

    const [currentLineStyleKey, currentLineStyles] = useMemo(() => {
      switch (calendarType) {
        case CalendarType.Day:
          return [nameof('dayLineStyles'), dayLineStyles];
        case CalendarType.Week:
          return [nameof('weekLineStyles'), weekLineStyles];
        case CalendarType.Month:
          return [nameof('monthLineStyles'), monthLineStyles];
        default:
          return [nameof('weekLineStyles'), weekLineStyles];
      }
    }, [calendarType, dayLineStyles, monthLineStyles, weekLineStyles]);

    const onRemove = useEventCallback((index: number) => {
      if (!currentLineStyles) {
        return;
      }
      setValue(
        currentLineStyleKey,
        currentLineStyles
          .filter((_d, i) => i !== index)
          .map((data, index) => {
            return {
              ...data,
              position: index,
            };
          }),
        {
          shouldDirty: true,
        },
      );
    });

    const onEdit = useEventCallback(
      (updatedLineStyle: VisitCardLineStyleDto) => {
        if (!currentLineStyles) {
          return;
        }
        setValue(
          currentLineStyleKey,
          currentLineStyles.map((d, i) =>
            i === updatedLineStyle.position ? updatedLineStyle : d,
          ),
          {
            shouldDirty: true,
          },
        );
      },
    );

    const onAddNewLine = useEventCallback(() => {
      const newLineStyle: VisitCardLineStyleDto = {
        position: currentLineStyles.length,
        expression: '',
        fontSize: 12,
        paddingTop: 4,
        isBold: false,
      };
      setValue(
        currentLineStyleKey,
        [...(currentLineStyles ?? []), newLineStyle],
        {
          shouldDirty: true,
        },
      );
      currentLineStyles.forEach((x) => {
        clearErrors(`${currentLineStyleKey}.${x.position}.expression`);
      });
    });

    const onResetToDefault = useEventCallback(async () => {
      try {
        await QueryFactory.VisitCardSettingsClient.resetToDefault();
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.VisitCardSettingsQuery.getQueryKey(),
        });
        onReset();
      } catch (error: any) {
        await modals.showError({ text: error.message });
      }
    });

    const onReset = useEventCallback(() => {
      if (!visitCardSettingsQuery.data) {
        return;
      }
      reset(visitCardSettingsQuery.data, {
        keepDirtyValues: false,
        keepErrors: false,
      });
    });

    const isValidationErrors = useMemo(() => {
      return !isEmpty(form.formState.errors[currentLineStyleKey]);
    }, [form.formState.errors[currentLineStyleKey]]);

    const onSortEnd = useEventCallback(async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const items = currentLineStyles;
      const oldIndex = items.findIndex((x) => x.position + 1 == active.id);
      const newIndex = items.findIndex((x) => x.position + 1 == over.id);

      const newData = arrayMove(currentLineStyles, oldIndex, newIndex);
      newData.forEach((item, index) => {
        item.position = index;
      });
      setValue(currentLineStyleKey, newData, {
        shouldDirty: true,
      });
    });

    const isUnsavedChanges = Object.keys(dirtyFields).length > 0;

    const onCalendarTypeChange = useEventCallback(
      async (newCalendarType: CalendarType) => {
        if (isUnsavedChanges) {
          const result = await modals.showConfirm({
            title: i18n.t('change_calendar_type_confirmation.title'),
            text: i18n.t('change_calendar_type_confirmation.text'),
            okButtonText: i18n.t('change_calendar_type_confirmation.ok_button'),
          });
          if (result) {
            onReset();
            setCalendarType(newCalendarType);
          }
        } else {
          setCalendarType(newCalendarType);
        }
      },
    );

    return (
      <Loading
        loading={false}
        title={i18n.t('loading')}
        testId="visit-card-settings-page"
      >
        <form
          onSubmit={form.handleSubmitDefault}
          data-test-id="visit-card-settings-form"
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={styles.titleContainer}>
                <div className={styles.title}>{i18n.t('title')}</div>
                <div
                  className={styles.dropdown}
                  data-test-id="calendar-types-dropdown"
                >
                  <DropDownInput
                    options={calendarTypes}
                    value={calendarType}
                    getOptionLabel={(v) =>
                      i18next.t(
                        `calendar_configuration.calendar_types.${CalendarType[v]}` as any,
                      )
                    }
                    required={true}
                    onValueChanged={(value) => onCalendarTypeChange(value)}
                  />
                </div>
              </div>

              <div className={styles.options}>
                <UniLink
                  title={i18n.t('add_button')}
                  color={ButtonColor.Primary}
                  icon={<AddIcon />}
                  onClick={onAddNewLine}
                  className={styles.optionButton}
                  testId="add-new-line-button"
                />
                <UniLink
                  title={i18n.t('reset_to_default_button')}
                  color={ButtonColor.Primary}
                  onClick={onResetToDefault}
                  className={styles.optionButton}
                  testId="reset-to-default-button"
                />
                <Button
                  title={i18n.t('reset_button')}
                  onClick={onReset}
                  color={ButtonColor.Primary}
                  className={styles.optionButton}
                  disabled={!isUnsavedChanges}
                  testId="reset-button"
                />
                <SubmitButton
                  disabled={!isUnsavedChanges}
                  title={i18n.t('save_button')}
                  submitting={formState.isSubmitting}
                  className={styles.optionButton}
                  testId="save-button"
                />
              </div>
            </div>

            <div
              className={styles.dataContainer}
              data-test-id="visit-card-settings-data"
            >
              <VisitCardLineTable
                isNoRulesFound={false}
                data={currentLineStyles}
                onRemove={onRemove}
                onEdit={onEdit}
                onSortEnd={onSortEnd}
                expressionErrors={visitSearchErrors
                  .filter((x) => x.startsWith(expressionErrorString))
                  .map((x) => x.replace(expressionErrorString, ''))}
                form={form}
                lineStyleKey={currentLineStyleKey}
              />
              <PreviewPanel
                lineStyles={currentLineStyles}
                errors={visitSearchErrors}
                setErrors={setVisitSearchErrors}
                validationError={
                  isValidationErrors ? i18n.t('errors.validation_error') : ''
                }
              />
            </div>
          </div>
        </form>
      </Loading>
    );
  },
);
